/* ---------- Automationen: Start, Frist und Aufwand (0.61, Paket B Teil 2) ---------- */
/*
 * Eine Zeitsemantik für alle Regeln, gemeinsam für Server (Ausführung, Planung) und Browser (Vorschau):
 * - Start und Frist sind gleichwertig. Bezugspunkte wie bei den Erinnerungen (`triggerBase`): ein Termin
 *   mit Uhrzeit ist dieser Zeitpunkt; ganztägiger Start = 09:00, ganztägige Frist = Tagesende.
 * - Kalendertage („3 Kalendertage später“) behalten die Ortszeit über Sommerzeitwechsel hinweg, eine Dauer
 *   („24 Stunden später“) ist genau diese Zeitspanne.
 * - Fehlt ein Bezugstermin, gibt es kein Ergebnis („Wartet auf Datum“) – nie still „heute“.
 * - Aufwand: Schätzung, gebuchte Zeit (gespeicherte Einträge) und optional laufende Timer. Ohne positive
 *   Schätzung sind Prozentbedingungen nicht auswertbar (keine Division durch 0).
 */
import type { AutomationAction, AutomationCardState, AutomationPredicate, ConditionGroup } from './automation.js';
import { addLocalDays, localDateOf, stageTime, triggerBase, zonedTime, type ReminderOffset } from './reminders.js';
import { localClock } from './notify.js';
import { allDayIso } from './dates.js';
import { addWorkdays, HOLIDAY_REGION_NAME, type HolidayRegion } from './holidays.js';

export type DateWhich = 'start' | 'due';
/** 0.69: `workdays` = Arbeitstage (ohne Wochenende und, falls gewählt, Feiertage des Bundeslands) */
export type DateUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'workdays';
export type DateBase = 'now' | 'event' | 'start' | 'due' | 'fixed' | 'field';

export const DEFAULT_TIME_ZONE = 'Europe/Berlin';
/** Normale Verzögerung des Zeitplaners – kein Ausfall */
export const AUTOMATION_JOB_TOLERANCE_MIN = 10;
export const AUTOMATION_MAX_CATCH_UP_MIN = 24 * 60;

const UNIT_MIN: Record<'minutes' | 'hours', number> = { minutes: 1, hours: 60 };
const isCalendar = (u: DateUnit) => u === 'days' || u === 'weeks' || u === 'workdays';
/** Kalenderdatum um einen Abstand verschieben (Tage, Wochen oder Arbeitstage) */
const moveDay = (date: string, amount: number, u: DateUnit, holidays: HolidayRegion | null | undefined) =>
  u === 'workdays' ? addWorkdays(date, amount, { weekends: true, holidays: holidays ?? null }) : addLocalDays(date, calendarDays(amount, u));
const calendarDays = (amount: number, u: DateUnit) => (u === 'weeks' ? amount * 7 : amount);
const hhmmOf = (minutes: number) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/** Termin einer Karte (wie gespeichert) */
export interface CardDate {
  iso: string;
  hasTime: boolean;
}

/** Zeitpunkt, ab dem ein Termin als erreicht gilt (Bezug wie bei den Erinnerungen) */
export function dateBoundary(which: DateWhich, d: CardDate, tz: string): Date {
  return triggerBase(which, d.iso, d.hasTime, tz).instant;
}

export function cardDate(s: Pick<AutomationCardState, 'startAt' | 'startHasTime' | 'dueAt' | 'dueHasTime'>, which: DateWhich): CardDate | null {
  const iso = which === 'start' ? s.startAt : s.dueAt;
  if (!iso) return null;
  return { iso, hasTime: !!(which === 'start' ? s.startHasTime : s.dueHasTime) };
}

/** 0.81: Bezugsdatum eines „erreicht“-Auslösers – Start, Frist oder eigenes Datumsfeld (ganztägig) */
export function triggerCardDate(
  s: Pick<AutomationCardState, 'startAt' | 'startHasTime' | 'dueAt' | 'dueHasTime' | 'custom'>,
  t: { which: DateWhich; fieldId?: string | null },
): { which: DateWhich; date: CardDate | null } {
  if (!t.fieldId) return { which: t.which, date: cardDate(s, t.which) };
  const v = s.custom[t.fieldId];
  return { which: 'start', date: typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? { iso: allDayIso(v), hasTime: false } : null };
}

/** Zeitpunkt eines „Termin erreicht“-Auslösers (mit Abstand) oder null ohne Termin */
export function dateReachedAt(which: DateWhich, offset: ReminderOffset, d: CardDate | null, tz: string): Date | null {
  if (!d) return null;
  return stageTime(offset, triggerBase(which, d.iso, d.hasTime, tz), tz);
}

/** Nächste Wiederholung: Stunden als Dauer, Tage als Kalendertage zur selben Ortszeit */
export function nextRepeatAt(prev: Date, repeat: { every: number; unit: 'hours' | 'days' }, tz: string): Date {
  if (repeat.unit === 'hours') return new Date(prev.getTime() + repeat.every * 3_600_000);
  const c = localClock(prev, tz);
  return zonedTime(addLocalDays(c.date, repeat.every), hhmmOf(c.minutes), tz);
}

/* ---------- Aufwand ---------- */

/** Gebuchte Minuten (gespeicherte Einträge), optional mit laufenden Timern bis `now` */
export function spentMinutes(s: Pick<AutomationCardState, 'spentMinutes' | 'running'>, now: number, includeRunning: boolean): number {
  const booked = s.spentMinutes ?? 0;
  if (!includeRunning) return booked;
  return booked + (s.running ?? []).reduce((n, iso) => n + Math.max(0, now - new Date(iso).getTime()) / 60_000, 0);
}

/** Schwelle einer Aufwandsbedingung in Minuten; null = nicht auswertbar (keine positive Schätzung) */
export function effortThreshold(p: Extract<AutomationPredicate, { type: 'effort' }>, s: Pick<AutomationCardState, 'estimateMinutes'>): number | null {
  if (p.measure === 'minutes') return p.value;
  const est = s.estimateMinutes ?? 0;
  if (!(est > 0)) return null;
  return (est * p.value) / 100;
}

/* ---------- Zeitabhängige Bedingungen ---------- */

const nowOf = (s: AutomationCardState) => s.now ?? Date.now();
const tzOf = (s: AutomationCardState) => s.timeZone ?? DEFAULT_TIME_ZONE;

export function timePredicateMatches(p: AutomationPredicate, s: AutomationCardState): boolean {
  const now = nowOf(s);
  switch (p.type) {
    case 'dateWindow': {
      const d = cardDate(s, p.which);
      if (!d) return false;
      const b = dateBoundary(p.which, d, tzOf(s)).getTime();
      const m = (p.minutes ?? 0) * 60_000;
      if (p.op === 'reached') return b <= now;
      if (p.op === 'notReached') return b > now;
      if (p.op === 'within') return b > now && b <= now + m;
      return b + m <= now; // overBy
    }
    case 'effort': {
      const t = effortThreshold(p, s);
      if (t === null) return false;
      const spent = spentMinutes(s, now, p.includeRunning);
      return p.op === 'gte' ? spent >= t : spent < t;
    }
    case 'estimate':
      return p.op === 'set' ? (s.estimateMinutes ?? 0) > 0 : !((s.estimateMinutes ?? 0) > 0);
    case 'dwell':
    case 'inactive': {
      const since = sinceOf(p, s);
      if (since === null) return false;
      const reached = since + p.minutes * 60_000 <= now;
      return p.op === 'atLeast' ? reached : !reached;
    }
    default:
      return false;
  }
}

/** 0.62: Bezug für Verweildauer bzw. Inaktivität (ms) */
function sinceOf(p: Extract<AutomationPredicate, { type: 'dwell' | 'inactive' }>, s: AutomationCardState): number | null {
  const iso = p.type === 'dwell' ? s.columnEnteredAt : s.lastActivityAt;
  return iso ? new Date(iso).getTime() : null;
}

export const isTimePredicate = (p: AutomationPredicate) => p.type === 'dateWindow' || (p.type === 'effort' && p.includeRunning) || p.type === 'dwell' || p.type === 'inactive';

export function groupHasTimePredicate(g: ConditionGroup): boolean {
  return g.items.some((x) => ('items' in x ? groupHasTimePredicate(x) : isTimePredicate(x)));
}

/**
 * Nächster Zeitpunkt nach `now`, an dem sich eine zeitabhängige Bedingung ändern kann (Termin wird
 * erreicht, Fenster beginnt/endet, laufender Timer erreicht die Schwelle). Danach plant der Server eine
 * erneute Prüfung – nicht minütlich, sondern genau an der nächsten Grenze.
 */
export function nextTimeBoundary(g: ConditionGroup, s: AutomationCardState): number | null {
  const now = nowOf(s);
  let best: number | null = null;
  const take = (t: number) => {
    if (t > now && (best === null || t < best)) best = t;
  };
  const walk = (x: ConditionGroup) => {
    for (const p of x.items) {
      if ('items' in p) {
        walk(p);
        continue;
      }
      if (p.type === 'dateWindow') {
        const d = cardDate(s, p.which);
        if (!d) continue;
        const b = dateBoundary(p.which, d, tzOf(s)).getTime();
        const m = (p.minutes ?? 0) * 60_000;
        take(b);
        if (p.op === 'within') take(b - m);
        if (p.op === 'overBy') take(b + m);
      }
      if (p.type === 'dwell' || p.type === 'inactive') {
        const since = sinceOf(p, s);
        if (since !== null) take(since + p.minutes * 60_000);
      }
      if (p.type === 'effort' && p.includeRunning) {
        const t = effortThreshold(p, s);
        const n = (s.running ?? []).length;
        if (t === null || !n) continue;
        const spent = spentMinutes(s, now, true);
        // eine Sekunde Puffer, damit die Prüfung sicher hinter der Schwelle liegt
        if (spent < t) take(Math.ceil(now + ((t - spent) / n) * 60_000) + 1000);
      }
    }
  };
  walk(g);
  return best;
}

/* ---------- Termine setzen und verschieben ---------- */

export type DateResult = { ok: true; value: CardDate | null; changed: boolean } | { ok: false; waiting: string; error?: boolean };

interface DateCtx {
  now: Date;
  /** Zeitpunkt des Auslösers (bei Zeitjobs der geplante Zeitpunkt, sonst jetzt) */
  eventAt?: Date;
  tz: string;
}

type CardDates = Pick<AutomationCardState, 'startAt' | 'startHasTime' | 'dueAt' | 'dueHasTime' | 'custom'>;

/** Bezugszeitpunkt: Zeitpunkt, lokales Datum und ob eine Uhrzeit dazugehört */
function resolveBase(a: Extract<AutomationAction, { type: 'setDate' }>, s: CardDates, c: DateCtx): { instant: Date; date: string; hasTime: boolean } | string {
  switch (a.base) {
    case 'now':
    case 'event': {
      const t = a.base === 'event' ? (c.eventAt ?? c.now) : c.now;
      return { instant: t, date: localDateOf(t, c.tz), hasTime: true };
    }
    case 'start':
    case 'due': {
      const d = cardDate(s, a.base);
      if (!d) return a.base === 'start' ? 'Wartet auf Datum: Der Start fehlt.' : 'Wartet auf Datum: Die Frist fehlt.';
      const b = triggerBase(a.base, d.iso, d.hasTime, c.tz);
      return { instant: b.instant, date: b.date, hasTime: d.hasTime };
    }
    case 'field': {
      const v = a.baseFieldId ? s.custom[a.baseFieldId] : null;
      if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(v)) return 'Wartet auf Datum: Das Datumsfeld ist leer.';
      const date = v.slice(0, 10);
      return { instant: zonedTime(date, '00:00', c.tz), date, hasTime: false };
    }
    case 'fixed': {
      const date = a.baseDate ?? '';
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return 'Wartet auf Datum: Kein fester Termin gewählt.';
      return a.baseTime ? { instant: zonedTime(date, a.baseTime, c.tz), date, hasTime: true } : { instant: zonedTime(date, '00:00', c.tz), date, hasTime: false };
    }
  }
}

const same = (x: CardDate | null, y: CardDate | null) => (x?.iso ?? null) === (y?.iso ?? null) && (x?.hasTime ?? false) === (y?.hasTime ?? false);

/** Ergebnis von „Termin setzen“ (ohne zu schreiben) – Ausführung und Vorschau rechnen gleich */
export function computeSetDate(a: Extract<AutomationAction, { type: 'setDate' }>, s: CardDates, c: DateCtx): DateResult {
  const current: CardDate | null =
    a.which === 'field' ? (typeof s.custom[a.fieldId ?? ''] === 'string' ? { iso: allDayIso(String(s.custom[a.fieldId ?? '']).slice(0, 10)), hasTime: false } : null) : cardDate(s, a.which);
  if (a.onlyIfEmpty && current) return { ok: true, value: current, changed: false };
  const base = resolveBase(a, s, c);
  if (typeof base === 'string') return { ok: false, waiting: base };
  let value: CardDate;
  if (isCalendar(a.unit)) {
    const date = moveDay(base.date, a.amount, a.unit, a.holidays);
    if (a.time === 'allDay' || a.which === 'field' || (a.time === 'base' && !base.hasTime)) value = { iso: allDayIso(date), hasTime: false };
    else {
      const hhmm = a.time === 'base' ? hhmmOf(localClock(base.instant, c.tz).minutes) : a.time;
      value = { iso: zonedTime(date, hhmm, c.tz).toISOString(), hasTime: true };
    }
  } else {
    const t = new Date(base.instant.getTime() + a.amount * UNIT_MIN[a.unit as 'minutes' | 'hours'] * 60_000);
    value = a.which === 'field' ? { iso: allDayIso(localDateOf(t, c.tz)), hasTime: false } : { iso: t.toISOString(), hasTime: true };
  }
  return { ok: true, value, changed: !same(current, value) };
}

/** Ergebnis von „Termin verschieben“ */
export function computeShiftDate(a: Extract<AutomationAction, { type: 'shiftDate' }>, s: CardDates, c: DateCtx): DateResult {
  const d = cardDate(s, a.which);
  if (!d) return { ok: false, waiting: a.which === 'start' ? 'Wartet auf Datum: Der Start fehlt.' : 'Wartet auf Datum: Die Frist fehlt.' };
  if (a.amount === 0) return { ok: true, value: d, changed: false };
  if (isCalendar(a.unit)) {
    if (!d.hasTime) return { ok: true, value: { iso: allDayIso(moveDay(d.iso.slice(0, 10), a.amount, a.unit, a.holidays)), hasTime: false }, changed: true };
    const cl = localClock(new Date(d.iso), c.tz);
    return { ok: true, value: { iso: zonedTime(moveDay(cl.date, a.amount, a.unit, a.holidays), hhmmOf(cl.minutes), c.tz).toISOString(), hasTime: true }, changed: true };
  }
  if (!d.hasTime) return { ok: false, waiting: 'Ein ganztägiger Termin lässt sich nur um Tage oder Wochen verschieben.', error: true };
  return { ok: true, value: { iso: new Date(new Date(d.iso).getTime() + a.amount * UNIT_MIN[a.unit as 'minutes' | 'hours'] * 60_000).toISOString(), hasTime: true }, changed: true };
}

/* ---------- Lesbare Texte ---------- */

export const DATE_NAME: Record<DateWhich, { nom: string; dat: string; acc: string }> = {
  start: { nom: 'der Start', dat: 'dem Start', acc: 'den Start' },
  due: { nom: 'die Frist', dat: 'der Frist', acc: 'die Frist' },
};

export function minutesText(m: number): string {
  const v = Math.round(Math.abs(m));
  if (v % 1440 === 0 && v >= 1440) return v === 1440 ? '1 Tag' : `${v / 1440} Tage`;
  if (v % 60 === 0 && v >= 60) return v === 60 ? '1 Stunde' : `${v / 60} Stunden`;
  if (v > 60) return `${Math.floor(v / 60)} Std. ${v % 60} Min.`;
  return v === 1 ? '1 Minute' : `${v} Minuten`;
}

export function unitText(n: number, u: DateUnit): string {
  const a = Math.abs(n);
  const w = { minutes: ['Minute', 'Minuten'], hours: ['Stunde', 'Stunden'], days: ['Kalendertag', 'Kalendertage'], weeks: ['Woche', 'Wochen'], workdays: ['Arbeitstag', 'Arbeitstage'] }[u];
  return `${a} ${a === 1 ? w[0] : w[1]}`;
}

/** „2 Tage vor der Frist“, „am Kalendertag vor dem Start um 09:00 Uhr“ … */
export function dateOffsetText(which: DateWhich, o: ReminderOffset, fieldName?: string): string {
  // 0.81: eigenes Datumsfeld statt Start/Frist
  const n = fieldName ? { nom: `„${fieldName}“`, dat: `„${fieldName}“`, acc: `„${fieldName}“` } : DATE_NAME[which];
  if (o.mode === 'at' || (o.mode === 'duration' && o.minutes === 0)) return `${n.nom} erreicht ist`;
  if (o.mode === 'duration') return `es ${minutesText(o.minutes)} ${o.minutes < 0 ? 'vor' : 'nach'} ${n.dat} ist`;
  const d = Math.abs(o.days);
  if (o.days === 0) return `es am Tag ${fieldName ? `von ${n.nom}` : which === 'due' ? 'der Frist' : 'des Starts'} ${o.time} Uhr ist`;
  if (o.mode === 'workdays')
    return `es ${d === 1 ? 'am Arbeitstag' : `${d} Arbeitstage`} ${o.days < 0 ? 'vor' : 'nach'} ${n.dat} um ${o.time} Uhr ist${o.holidays ? ` (ohne Wochenenden und Feiertage in ${HOLIDAY_REGION_NAME[o.holidays]})` : ' (ohne Wochenenden)'}`;
  return `es ${d === 1 ? 'am Kalendertag' : `${d} Kalendertage`} ${o.days < 0 ? 'vor' : 'nach'} ${n.dat} um ${o.time} Uhr ist`;
}

export function formatInstantDe(iso: string, hasTime: boolean, tz: string): string {
  const d = new Date(iso);
  if (!hasTime) return d.toLocaleDateString('de-DE', { timeZone: 'UTC', weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
  return `${d.toLocaleString('de-DE', { timeZone: tz, weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr`;
}
